import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  IconButton,
  Collapse,
  FormControlLabel,
  Switch,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { digitalGuideApi, DigitalGuide, UpdateDigitalGuideDto } from '../api/digitalGuide';

const DigitalGuidePage: React.FC = () => {
  const queryClient = useQueryClient();
  const [expandedSections, setExpandedSections] = useState({
    title: true,
    description: true,
    steps: true,
    benefits: true,
    settings: true,
  });

  const [formData, setFormData] = useState<UpdateDigitalGuideDto>({
    content: {
      en: {
        title: '',
        subtitle: '',
        description: '',
        transformationSteps: '',
        transformationStepsSubtitle: '',
        digitalTransformationBenefits: '',
        digitalTransformationBenefitsSubtitle: '',
        steps: [],
        benefits: [],
      },
      ru: {
        title: '',
        subtitle: '',
        description: '',
        transformationSteps: '',
        transformationStepsSubtitle: '',
        digitalTransformationBenefits: '',
        digitalTransformationBenefitsSubtitle: '',
        steps: [],
        benefits: [],
      },
    },
    isActive: true,
  });

  const [isActive, setIsActive] = useState(true);

  // Fetch digital guide data
  const { data: digitalGuide, isLoading, error } = useQuery({
    queryKey: ['digitalGuide'],
    queryFn: digitalGuideApi.getMain,
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: digitalGuideApi.updateMain,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['digitalGuide'] });
    },
  });

  // Initialize mutation
  const initializeMutation = useMutation({
    mutationFn: digitalGuideApi.initialize,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['digitalGuide'] });
    },
  });

  // Load data into form
  useEffect(() => {
    if (digitalGuide) {
      setFormData({
        content: {
          en: {
            title: digitalGuide.content.en.title || '',
            subtitle: digitalGuide.content.en.subtitle || '',
            description: digitalGuide.content.en.description || '',
            transformationSteps: digitalGuide.content.en.transformationSteps || '',
            transformationStepsSubtitle: digitalGuide.content.en.transformationStepsSubtitle || '',
            digitalTransformationBenefits: digitalGuide.content.en.digitalTransformationBenefits || '',
            digitalTransformationBenefitsSubtitle: digitalGuide.content.en.digitalTransformationBenefitsSubtitle || '',
            steps: digitalGuide.content.en.steps || [],
            benefits: digitalGuide.content.en.benefits || [],
          },
          ru: {
            title: digitalGuide.content.ru.title || '',
            subtitle: digitalGuide.content.ru.subtitle || '',
            description: digitalGuide.content.ru.description || '',
            transformationSteps: digitalGuide.content.ru.transformationSteps || '',
            transformationStepsSubtitle: digitalGuide.content.ru.transformationStepsSubtitle || '',
            digitalTransformationBenefits: digitalGuide.content.ru.digitalTransformationBenefits || '',
            digitalTransformationBenefitsSubtitle: digitalGuide.content.ru.digitalTransformationBenefitsSubtitle || '',
            steps: digitalGuide.content.ru.steps || [],
            benefits: digitalGuide.content.ru.benefits || [],
          },
        },
        isActive: digitalGuide.isActive,
      });
      setIsActive(digitalGuide.isActive);
    }
  }, [digitalGuide]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await updateMutation.mutateAsync({
        ...formData,
        isActive,
      });
    } catch (error) {
      console.error('Error updating digital guide:', error);
    }
  };

  const handleInitialize = async () => {
    try {
      await initializeMutation.mutateAsync();
    } catch (error) {
      console.error('Error initializing digital guide:', error);
    }
  };

  const addStep = (language: 'en' | 'ru') => {
    setFormData(prev => ({
      ...prev,
      content: {
        ...prev.content!,
        [language]: {
          ...prev.content![language],
          steps: [
            ...prev.content![language].steps,
            {
              title: '',
              description: '',
              icon: 'Lightbulb',
            },
          ],
        },
      },
    }));
  };

  const updateStep = (language: 'en' | 'ru', index: number, field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      content: {
        ...prev.content!,
        [language]: {
          ...prev.content![language],
          steps: prev.content![language].steps.map((step, i) =>
            i === index ? { ...step, [field]: value } : step
          ),
        },
      },
    }));
  };

  const removeStep = (language: 'en' | 'ru', index: number) => {
    setFormData(prev => ({
      ...prev,
      content: {
        ...prev.content!,
        [language]: {
          ...prev.content![language],
          steps: prev.content![language].steps.filter((_, i) => i !== index),
        },
      },
    }));
  };

  const addBenefit = (language: 'en' | 'ru') => {
    setFormData(prev => ({
      ...prev,
      content: {
        ...prev.content!,
        [language]: {
          ...prev.content![language],
          benefits: [...prev.content![language].benefits, ''],
        },
      },
    }));
  };

  const updateBenefit = (language: 'en' | 'ru', index: number, value: string) => {
    setFormData(prev => ({
      ...prev,
      content: {
        ...prev.content!,
        [language]: {
          ...prev.content![language],
          benefits: prev.content![language].benefits.map((benefit, i) =>
            i === index ? value : benefit
          ),
        },
      },
    }));
  };

  const removeBenefit = (language: 'en' | 'ru', index: number) => {
    setFormData(prev => ({
      ...prev,
      content: {
        ...prev.content!,
        [language]: {
          ...prev.content![language],
          benefits: prev.content![language].benefits.filter((_, i) => i !== index),
        },
      },
    }));
  };

  const iconOptions = [
    'Lightbulb',
    'TrendingUp',
    'Users',
    'Globe',
    'Smartphone',
    'Zap',
    'Code',
    'Settings',
    'Rocket',
    'Target',
  ];

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">
          Error loading digital guide data. Please try again.
        </Alert>
        <Button
          variant="contained"
          onClick={handleInitialize}
          sx={{ mt: 2 }}
          disabled={initializeMutation.isPending}
        >
          {initializeMutation.isPending ? 'Initializing...' : 'Initialize Digital Guide'}
        </Button>
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Typography variant="h4" gutterBottom>
        Digital Transformation Guide Management
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Manage the content for the "Don't Know How to Go Digital?" section on the main page.
      </Typography>

      <form onSubmit={handleSubmit}>
        {/* Title Section */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" sx={{ flexGrow: 1 }}>
                Title & Subtitle
              </Typography>
              <IconButton
                onClick={() => setExpandedSections(prev => ({ ...prev, title: !prev.title }))}
              >
                {expandedSections.title ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Box>

            <Collapse in={expandedSections.title}>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    English
                  </Typography>
                  <TextField
                    fullWidth
                    label="Title (English)"
                    value={formData.content?.en.title || ''}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      content: { ...prev.content!, en: { ...prev.content!.en, title: e.target.value } }
                    }))}
                    sx={{ mb: 2 }}
                  />
                  <TextField
                    fullWidth
                    label="Subtitle (English)"
                    value={formData.content?.en.subtitle || ''}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      content: { ...prev.content!, en: { ...prev.content!.en, subtitle: e.target.value } }
                    }))}
                  />
                </Box>
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Russian
                  </Typography>
                  <TextField
                    fullWidth
                    label="Title (Russian)"
                    value={formData.content?.ru.title || ''}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      content: { ...prev.content!, ru: { ...prev.content!.ru, title: e.target.value } }
                    }))}
                    sx={{ mb: 2 }}
                  />
                  <TextField
                    fullWidth
                    label="Subtitle (Russian)"
                    value={formData.content?.ru.subtitle || ''}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      content: { ...prev.content!, ru: { ...prev.content!.ru, subtitle: e.target.value } }
                    }))}
                  />
                </Box>
              </Box>
            </Collapse>
          </CardContent>
        </Card>

        {/* Description Section */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" sx={{ flexGrow: 1 }}>
                Main Description
              </Typography>
              <IconButton
                onClick={() => setExpandedSections(prev => ({ ...prev, description: !prev.description }))}
              >
                {expandedSections.description ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Box>

            <Collapse in={expandedSections.description}>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    English
                  </Typography>
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    label="Description (English)"
                    value={formData.content?.en.description || ''}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      content: { ...prev.content!, en: { ...prev.content!.en, description: e.target.value } }
                    }))}
                  />
                </Box>
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Russian
                  </Typography>
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    label="Description (Russian)"
                    value={formData.content?.ru.description || ''}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      content: { ...prev.content!, ru: { ...prev.content!.ru, description: e.target.value } }
                    }))}
                  />
                </Box>
              </Box>
            </Collapse>
          </CardContent>
        </Card>

        {/* Steps Section */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" sx={{ flexGrow: 1 }}>
                Transformation Steps
              </Typography>
              <IconButton
                onClick={() => setExpandedSections(prev => ({ ...prev, steps: !prev.steps }))}
              >
                {expandedSections.steps ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Box>

            <Collapse in={expandedSections.steps}>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
                {/* English Steps */}
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    English Steps
                  </Typography>
                  <TextField
                    fullWidth
                    label="Steps Title (English)"
                    value={formData.content?.en.transformationSteps || ''}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      content: { ...prev.content!, en: { ...prev.content!.en, transformationSteps: e.target.value } }
                    }))}
                    sx={{ mb: 2 }}
                  />
                  <TextField
                    fullWidth
                    label="Steps Subtitle (English)"
                    value={formData.content?.en.transformationStepsSubtitle || ''}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      content: { ...prev.content!, en: { ...prev.content!.en, transformationStepsSubtitle: e.target.value } }
                    }))}
                    sx={{ mb: 2 }}
                  />
                  
                  {formData.content?.en.steps.map((step, index) => (
                    <Card key={index} sx={{ mb: 2, p: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="subtitle2">Step {index + 1}</Typography>
                        <IconButton
                          size="small"
                          onClick={() => removeStep('en', index)}
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                      <TextField
                        fullWidth
                        label="Title"
                        value={step.title}
                        onChange={(e) => updateStep('en', index, 'title', e.target.value)}
                        sx={{ mb: 2 }}
                      />
                      <TextField
                        fullWidth
                        multiline
                        rows={3}
                        label="Description"
                        value={step.description}
                        onChange={(e) => updateStep('en', index, 'description', e.target.value)}
                        sx={{ mb: 2 }}
                      />
                      <TextField
                        fullWidth
                        select
                        label="Icon"
                        value={step.icon}
                        onChange={(e) => updateStep('en', index, 'icon', e.target.value)}
                        SelectProps={{
                          native: true,
                        }}
                      >
                        {iconOptions.map((icon) => (
                          <option key={icon} value={icon}>
                            {icon}
                          </option>
                        ))}
                      </TextField>
                    </Card>
                  ))}
                  
                  <Button
                    startIcon={<AddIcon />}
                    onClick={() => addStep('en')}
                    variant="outlined"
                    sx={{ mt: 1 }}
                  >
                    Add Step
                  </Button>
                </Box>

                {/* Russian Steps */}
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Russian Steps
                  </Typography>
                  <TextField
                    fullWidth
                    label="Steps Title (Russian)"
                    value={formData.content?.ru.transformationSteps || ''}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      content: { ...prev.content!, ru: { ...prev.content!.ru, transformationSteps: e.target.value } }
                    }))}
                    sx={{ mb: 2 }}
                  />
                  <TextField
                    fullWidth
                    label="Steps Subtitle (Russian)"
                    value={formData.content?.ru.transformationStepsSubtitle || ''}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      content: { ...prev.content!, ru: { ...prev.content!.ru, transformationStepsSubtitle: e.target.value } }
                    }))}
                    sx={{ mb: 2 }}
                  />
                  
                  {formData.content?.ru.steps.map((step, index) => (
                    <Card key={index} sx={{ mb: 2, p: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="subtitle2">Step {index + 1}</Typography>
                        <IconButton
                          size="small"
                          onClick={() => removeStep('ru', index)}
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                      <TextField
                        fullWidth
                        label="Title"
                        value={step.title}
                        onChange={(e) => updateStep('ru', index, 'title', e.target.value)}
                        sx={{ mb: 2 }}
                      />
                      <TextField
                        fullWidth
                        multiline
                        rows={3}
                        label="Description"
                        value={step.description}
                        onChange={(e) => updateStep('ru', index, 'description', e.target.value)}
                        sx={{ mb: 2 }}
                      />
                      <TextField
                        fullWidth
                        select
                        label="Icon"
                        value={step.icon}
                        onChange={(e) => updateStep('ru', index, 'icon', e.target.value)}
                        SelectProps={{
                          native: true,
                        }}
                      >
                        {iconOptions.map((icon) => (
                          <option key={icon} value={icon}>
                            {icon}
                          </option>
                        ))}
                      </TextField>
                    </Card>
                  ))}
                  
                  <Button
                    startIcon={<AddIcon />}
                    onClick={() => addStep('ru')}
                    variant="outlined"
                    sx={{ mt: 1 }}
                  >
                    Add Step
                  </Button>
                </Box>
              </Box>
            </Collapse>
          </CardContent>
        </Card>

        {/* Benefits Section */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" sx={{ flexGrow: 1 }}>
                Benefits
              </Typography>
              <IconButton
                onClick={() => setExpandedSections(prev => ({ ...prev, benefits: !prev.benefits }))}
              >
                {expandedSections.benefits ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Box>

            <Collapse in={expandedSections.benefits}>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
                {/* English Benefits */}
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    English Benefits
                  </Typography>
                  <TextField
                    fullWidth
                    label="Benefits Title (English)"
                    value={formData.content?.en.digitalTransformationBenefits || ''}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      content: { ...prev.content!, en: { ...prev.content!.en, digitalTransformationBenefits: e.target.value } }
                    }))}
                    sx={{ mb: 2 }}
                  />
                  <TextField
                    fullWidth
                    label="Benefits Subtitle (English)"
                    value={formData.content?.en.digitalTransformationBenefitsSubtitle || ''}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      content: { ...prev.content!, en: { ...prev.content!.en, digitalTransformationBenefitsSubtitle: e.target.value } }
                    }))}
                    sx={{ mb: 2 }}
                  />
                  
                  {formData.content?.en.benefits.map((benefit, index) => (
                    <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <TextField
                        fullWidth
                        label={`Benefit ${index + 1}`}
                        value={benefit}
                        onChange={(e) => updateBenefit('en', index, e.target.value)}
                        sx={{ mr: 1 }}
                      />
                      <IconButton
                        size="small"
                        onClick={() => removeBenefit('en', index)}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  ))}
                  
                  <Button
                    startIcon={<AddIcon />}
                    onClick={() => addBenefit('en')}
                    variant="outlined"
                    sx={{ mt: 1 }}
                  >
                    Add Benefit
                  </Button>
                </Box>

                {/* Russian Benefits */}
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Russian Benefits
                  </Typography>
                  <TextField
                    fullWidth
                    label="Benefits Title (Russian)"
                    value={formData.content?.ru.digitalTransformationBenefits || ''}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      content: { ...prev.content!, ru: { ...prev.content!.ru, digitalTransformationBenefits: e.target.value } }
                    }))}
                    sx={{ mb: 2 }}
                  />
                  <TextField
                    fullWidth
                    label="Benefits Subtitle (Russian)"
                    value={formData.content?.ru.digitalTransformationBenefitsSubtitle || ''}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      content: { ...prev.content!, ru: { ...prev.content!.ru, digitalTransformationBenefitsSubtitle: e.target.value } }
                    }))}
                    sx={{ mb: 2 }}
                  />

                  {formData.content?.ru.benefits.map((benefit, index) => (
                    <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <TextField
                        fullWidth
                        label={`Benefit ${index + 1}`}
                        value={benefit}
                        onChange={(e) => updateBenefit('ru', index, e.target.value)}
                        sx={{ mr: 1 }}
                      />
                      <IconButton
                        size="small"
                        onClick={() => removeBenefit('ru', index)}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  ))}

                  <Button
                    startIcon={<AddIcon />}
                    onClick={() => addBenefit('ru')}
                    variant="outlined"
                    sx={{ mt: 1 }}
                  >
                    Add Benefit
                  </Button>
                </Box>
              </Box>
            </Collapse>
          </CardContent>
        </Card>

        {/* Settings Section */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" sx={{ flexGrow: 1 }}>
                Settings
              </Typography>
              <IconButton
                onClick={() => setExpandedSections(prev => ({ ...prev, settings: !prev.settings }))}
              >
                {expandedSections.settings ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Box>

            <Collapse in={expandedSections.settings}>
              <FormControlLabel
                control={
                  <Switch
                    checked={isActive}
                    onChange={(e) => setIsActive(e.target.checked)}
                  />
                }
                label="Active"
              />
            </Collapse>
          </CardContent>
        </Card>

        {/* Submit Button */}
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <Button
            type="submit"
            variant="contained"
            disabled={updateMutation.isPending}
            sx={{ minWidth: 120 }}
          >
            {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
          </Button>
        </Box>
      </form>

      {/* Success/Error Messages */}
      {updateMutation.isSuccess && (
        <Alert severity="success" sx={{ mt: 2 }}>
          Digital guide updated successfully!
        </Alert>
      )}

      {updateMutation.isError && (
        <Alert severity="error" sx={{ mt: 2 }}>
          Error updating digital guide. Please try again.
        </Alert>
      )}
    </Box>
  );
};

export default DigitalGuidePage;